import { getCountries } from 'react-phone-number-input';
import { FieldBuilderComponentPropsType } from '../types';
import { Field } from './primitive/Field';
import { PhoneInput } from './primitive/PhoneInput/PhoneInput';
import { PhoneNumberFlagCountryCodes } from './primitive/PhoneInput/types';
import { InputGroup } from './primitive/InputGroup';
import { AtSign, Building2, Globe } from 'lucide-react';
import { Textarea } from './primitive/Input';
import { use, useCallback, useId } from 'react';
import { SliderInput } from './primitive/SliderInput/SliderInput';
import { useFormContext } from 'react-hook-form';
import { FormManifestContext } from '../FormBuilder';
import { parseFullName } from 'parse-full-name';
import { tinaField } from 'tinacms/react';
export function TextInput({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsInputField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <Field.Control required placeholder={data.placeholder ?? ''} />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function NumberInput({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsNumberField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <Field.Control required inputMode="numeric" />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function FullNameInput({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsFullNameField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <Field.Control required placeholder={data.placeholder ?? ''} />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function Phone({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsPhoneNumberField'>) {
  const id = useId();
  return <Field.Root name={name}>
      <Field.Label htmlFor={`${id}-phone-input`} data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <PhoneInput inputId={`${id}-phone-input`} placeholder={data.placeholder ?? ''} defaultCountry={'AU'} countryCodes={getCountries() as PhoneNumberFlagCountryCodes[]} showCountrySelector={true} />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function Email({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsEmailField'>) {
  const formContext = useFormContext();
  const manifest = use(FormManifestContext);
  const fullNameField = manifest.flattenedFields.find(field => field.__typename === 'ConversionFormStepsFieldsFullNameField');
  const handleBlur = useCallback(function () {
    const [email, fullName] = formContext.getValues([name, fullNameField?.id ?? '']);
    const parsed = parseFullName(fullName);
    if (email) {
      const _hsq = window._hsq = window._hsq || [];
      _hsq.push(['identify', {
        email: email,
        firstname: parsed.first,
        lastname: parsed.last
      }]);
      _hsq.push(['trackPageView']);
    }
  }, [formContext, name, fullNameField]);
  return <Field.Root name={name} onBlur={handleBlur}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <InputGroup.Root>
        <InputGroup.Input placeholder={data.placeholder ?? ''} autoCapitalize="none" autoCorrect="off" type="email" spellCheck="false" inputMode="email" />
        <InputGroup.Addon align={'inline-start'}>
          <AtSign size="1em" aria-hidden />
        </InputGroup.Addon>
      </InputGroup.Root>
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function CompanyName({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsCompanyNameField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <InputGroup.Root>
        <InputGroup.Input placeholder={data.placeholder ?? ''} />
        <InputGroup.Addon align={'inline-start'}>
          <Building2 size="1em" aria-hidden />
        </InputGroup.Addon>
      </InputGroup.Root>
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function URL({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsUrlField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <InputGroup.Root>
        <InputGroup.Input placeholder={data.placeholder ?? ''} autoCapitalize="none" autoCorrect="off" type="url" spellCheck="false" inputMode="url" />
        <InputGroup.Addon align={'inline-start'}>
          <Globe size="1em" aria-hidden />
        </InputGroup.Addon>
      </InputGroup.Root>
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function Message({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsMessageField'>) {
  return <Field.Root name={name}>
      <Field.Label data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </Field.Label>
      <Textarea linesHigh={6} placeholder={data.placeholder ?? ''} />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}
export function Slider({
  data,
  name
}: FieldBuilderComponentPropsType<'ConversionFormStepsFieldsSliderField'>) {
  return <Field.Root name={name}>
      <SliderInput data={data} />
      <Field.Error />
      <Field.Description>{data.hint}</Field.Description>
    </Field.Root>;
}